/**
 * Pack hour + layout into one frame of the GPU sky. Pure JS — no Pixi, no canvas.
 *
 * The Pixi sky layer (render/pixi/sky-shader.js) draws the sky bands and the sea with two
 * small shaders and places the sun/moon, stars and clouds as sprites. Everything it needs is
 * computed here from the same functions the Canvas 2D scene uses (band colours, sea rows,
 * celestial pose, cloud lobes and tones, star layout), so the two renderers match.
 */

import { sunAlpha } from "./celestial.js";
import { CLOUD_COUNT, layoutSkyClouds, cloudTones, cloudCenter, cloudBandBottomY } from "./clouds.js";
import { starFieldAlpha, STAR_DRIFT_PX_PER_SEC } from "./stars.js";
import { nightAmount } from "./sky-utils.js";
import { skyBandColors } from "./sky-gradient.js";
import {
  seaColors,
  seaBandEdge,
  layoutSeaFoam,
  layoutSeaGlare,
  glareColors,
  horizonLineWidth,
  HORIZON_SHEEN_WIDTH_AT_720,
  SEA_BAND_EDGES,
} from "./sea.js";
import {
  buildSkySceneContext,
  cloudDayBoundary,
  cloudWarm,
  skySceneFx,
} from "../sky-scene.js";

export var SKY_SHADER_CLOUD_MAX = CLOUD_COUNT;

var SOLID_DAY_RGB = [207 / 255, 239 / 255, 251 / 255];

function rgb01(c) {
  if (!c || !c.length) return [1, 1, 1];
  return [c[0] / 255, c[1] / 255, c[2] / 255];
}

/**
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} hour
 * @param {{
 *   layout?: object,
 *   runSeed?: number,
 *   drawStars?: boolean,
 *   drawClouds?: boolean,
 *   drawCelestial?: boolean,
 *   wobbleSec?: number,
 * }} [opts]
 */
export function buildSkyShaderUniforms(bounds, hour, opts) {
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) {
    return { solid: true, solidRgb: SOLID_DAY_RGB.slice() };
  }

  var state = scene.state;
  var t = scene.t;
  var night = nightAmount(t);
  var wobbleSec = scene.wobbleSec;
  var showCelestial = opts.drawCelestial !== false;
  var showClouds = opts.drawClouds !== false;
  var starsOn = opts.drawStars !== false;
  var starAlpha = starsOn ? starFieldAlpha(t) : 0;
  if (scene.skyBand.height <= 0) starAlpha = 0;

  var discVisible = showCelestial && scene.discVisible ? 1 : 0;
  var emergence = scene.emergence;
  var cloudLight = discVisible
    ? {
      x: scene.pos.x,
      y: scene.pos.y,
      glowRadius: scene.bodyRadius * 8,
      color: state.isDay ? [255, 232, 158] : [200, 220, 255],
      strength: (state.isDay ? 1.05 : 0.5) * (0.3 + 0.7 * emergence),
    }
    : { x: 0, y: 0, glowRadius: 0, color: [0, 0, 0], strength: 0 };

  var warm = cloudWarm(state.phase);
  var boundary = cloudDayBoundary(state.phase);
  var placed = showClouds
    ? layoutSkyClouds(bounds, scene.animMs, scene.cloudSeed, {
      layout: scene.layout,
      cloudBandBottomY: cloudBandBottomY(scene.layout, bounds),
      wobbleSec: wobbleSec,
    })
    : [];
  var look = {
    timeOfDay: t,
    cloudDayBoundary: boundary,
    cloudWarmth: warm.amount,
    cloudWarmColors: warm.colors,
    cloudLight: discVisible ? cloudLight : null,
  };
  var clouds = [];
  for (var i = 0; i < placed.length; i++) {
    var c = placed[i];
    clouds.push({
      seed: c.seed,
      ox: c.ox,
      oy: c.oy,
      bodyW: c.bodyW,
      bodyH: c.bodyH,
      ink: c.ink,
      lobes: c.lobes,
      alpha: 1,
      tones: cloudTones(look, cloudCenter(c)),
    });
  }

  var pose = scene.pose;
  var seaLineY = scene.seaLineY;
  var seaH = Math.max(0, bounds.y + bounds.height - seaLineY);
  var scaleUi = scene.scaleUi;
  var eased = emergence * emergence * (3 - 2 * emergence);
  var glare = discVisible && emergence > 0.001
    ? layoutSeaGlare({
      celestialX: scene.pos.x,
      seaLineY: seaLineY,
      seaH: seaH,
      scaleUi: scaleUi,
      isMoon: !state.isDay,
      strength: (state.isDay ? 1 : 0.85) * eased,
      widthScale: 0.22 + 0.78 * eased,
      waveTime: wobbleSec,
    })
    : [];
  var glareCols = glareColors(!state.isDay, pose.sunset);
  var fx = skySceneFx(scene, { drawCelestial: showCelestial, drawStars: starAlpha > 0.01 });
  var edges = [];
  for (var j = 0; j < SEA_BAND_EDGES.length; j++) {
    edges.push(seaBandEdge(j, seaLineY, seaH, scaleUi, wobbleSec));
  }

  return {
    solid: false,
    solidRgb: SOLID_DAY_RGB.slice(),
    width: bounds.width,
    height: bounds.height,
    colorPhase: t,
    seaLineY: seaLineY,
    bodyX: scene.pos.x,
    bodyY: scene.pos.y,
    bodyRadius: scene.bodyRadius,
    emergence: emergence,
    isDay: state.isDay ? 1 : 0,
    discVisible: discVisible,
    sunAlpha: state.isDay ? sunAlpha(t) : 1,
    animSec: scene.animSec,
    wobbleSec: wobbleSec,
    seed: scene.runSeed || 0,
    scaleUi: scaleUi,
    starAlpha: starAlpha,
    starDrift: scene.animSec * STAR_DRIFT_PX_PER_SEC,
    skyBandHeight: scene.skyBand.height,
    drawClouds: showClouds ? 1 : 0,
    drawCelestial: showCelestial ? 1 : 0,
    cloudBoundary: boundary,
    cloudWarmth: warm.amount,
    nightAmount: night,
    cloudCount: Math.min(SKY_SHADER_CLOUD_MAX, clouds.length),
    lightX: cloudLight.x,
    lightY: cloudLight.y,
    lightRadius: cloudLight.glowRadius,
    lightStrength: cloudLight.strength,
    lightRgb: rgb01(cloudLight.color),
    skyBands: skyBandColors(t).map(rgb01),
    sea: {
      colors: mapColors(seaColors(t)),
      edges: edges,
      foam: layoutSeaFoam(bounds.width, seaLineY, seaH, scaleUi, wobbleSec),
      glare: glare,
      glareTint: rgb01(glareCols.tint),
      glareCore: rgb01(glareCols.core),
      lineW: horizonLineWidth(scaleUi),
      sheenW: Math.max(1, HORIZON_SHEEN_WIDTH_AT_720 * scaleUi),
    },
    pose: showCelestial ? pose : null,
    clouds: clouds,
    sparkles: fx.sparkles,
    sparkleRgb: fx.sparkleRgb,
    shootingStar: fx.shootingStar,
  };
}

function mapColors(cols) {
  var out = {};
  for (var k in cols) {
    if (Object.prototype.hasOwnProperty.call(cols, k)) out[k] = rgb01(cols[k]);
  }
  return out;
}

export function skyShaderSolidUniforms() {
  return { solid: true, solidRgb: SOLID_DAY_RGB.slice() };
}
